import { AccountCircle as AccountCircleIcon, Menu as MenuIcon, Store as StoreIcon, } from '@mui/icons-material'
import { AppBar, Backdrop, Box, Container, CssBaseline, IconButton, LinearProgress, List, Menu, Snackbar, SwipeableDrawer, Toolbar, Tooltip, Typography } from '@mui/material'
import { blue, grey, yellow } from '@mui/material/colors'
import withStyles from '@mui/styles/withStyles'
import React from "react"
import { Route, Link as RouterLink, Switch, withRouter } from "react-router-dom"
import { CartIcon, Copyright, MiniRemoteUserPicker, NavListItem, NavMenuItem, PrivateRoute, RemoteUserPicker } from '../../components'
import { withAppContext } from '../../context/AppContextProvider'
import NotFound from '../404'
import Account from '../Account'
import Agenda from '../Agenda'
import Book from '../Book'
import Cart from '../Cart'
import ChangePassword from '../ChangePassword'
import LeadForm from '../LeadForm'
import Order from '../Order'
import Orders from '../Orders'
import PaymentMethods from '../PaymentMethods'
import VerifySMS from '../VerifySMS'
import Welcome from './Welcome'

const styles = (theme) => ({
    appBar: props => ({
        backgroundColor: props.features && props.features.title_bg_color ? props.features.title_bg_color : blue[700],
        color: props.features && props.features.title_font_color ? props.features.title_font_color : undefined,
    }),
    toolbar: theme.mixins.toolbar,
    title: {
        flexGrow: 1,
        textDecoration: 'none',
        color: 'inherit',
        '&:visited': {
            color: 'inherit',
        },
        '&:active': {
            color: 'inherit',
        },
    },
    drawerList: {
        minWidth: 200,
    },
    outerBox: {
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        flexWrap: 'nowrap',
        overflow: 'auto',
    },
    innerBox: {
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
    },
    connectingBox: {
        marginTop: theme.spacing(5),
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
    },
    reconnectingBackdrop: {
        zIndex: theme.zIndex.drawer + 1,
    },
    remoteUserAppBar: {
        backgroundColor: grey[200],
        zIndex: 'auto',
    },
    remoteUserAppBarPopulated: {
        backgroundColor: yellow[200],
        zIndex: 'auto',
    },
})

class App extends React.Component {
    state = {
        drawerOpen: false,
        storeMenuAnchor: null,
        accountMenuAnchor: null,
    }

    componentDidMount() {
        document.getElementById('pwa-manifest')?.setAttribute('href', `manifest.json?fileset=${this.props.match.params.fileset}&storeId=${this.props.match.params.storeId}`)
    }

    componentWillUnmount() {
        document.getElementById('pwa-manifest')?.removeAttribute('href')
    }

    componentDidUpdate(prevProps) {
        // scoll back to top on page change
        if (this.props.location !== prevProps.location) {
            document.documentElement.scrollTop = 0

            if (this.props.isEmbedded) {
                this.props.messageCompanion({
                    notifyEvent: 'pageChange',
                })
            }
        }
    }

    openDrawer = () => {
        this.setState({ drawerOpen: true })
    }

    closeDrawer = () => {
        this.setState({ drawerOpen: false })
    }

    handleStoreMenuClick = (event) => {
        this.setState({
            storeMenuAnchor: event.currentTarget,
        })
    }

    handleChangeStore = async (storeId) => {
        try {
            const response = await this.props.socket.send('changeStore', { storeId })

            this.props.showMessage(response, { variant: 'success' })

            await new Promise(res => setTimeout(res, 2000))

            window.location.href = `/${this.props.fileset}/s${storeId}`

        } catch (err) {
            this.props.showError(err)
        }
    }

    handleAccountMenuClick = (event) => {
        this.setState({
            accountMenuAnchor: event.currentTarget,
        })
    }

    closeStoreMenu = () => {
        this.setState({
            storeMenuAnchor: null,
        })
    }

    closeAccountMenu = () => {
        this.setState({
            accountMenuAnchor: null,
        })
    }

    render() {
        const { classes, history, paths, connected, initialized, store, stores, features, user, remoteUser } = this.props
        const { drawerOpen, storeMenuAnchor, accountMenuAnchor } = this.state

        if (!initialized) {
            return (
                <Container maxWidth="xs" sx={{ py: 10 }}>
                    <LinearProgress />
                </Container>
            )
        }

        const title = `${store.name} • Employee Portal`

        const langQuirks = {
            my: 'My',
        }

        if (remoteUser) {
            langQuirks.my = `${remoteUser.full_name}'s`
        }

        return <>
            <CssBaseline />
            <SwipeableDrawer open={drawerOpen} onClose={this.closeDrawer} onOpen={this.openDrawer}>
                <List className={classes.drawerList} component="nav">
                    <NavListItem to={paths.root} onClick={this.closeDrawer} primary="Home" exact />
                    <NavListItem to={paths.agenda} onClick={this.closeDrawer} primary="Agenda" />
                    <NavListItem to={paths.leadForm} onClick={this.closeDrawer} primary="Add a Lead" />
                    {features.ebooking &&
                        <NavListItem to={paths.book} onClick={this.closeDrawer} primary="Online Booking" />
                    }
                    {features.ecommerce && <>
                        <NavListItem to={paths.order} onClick={this.closeDrawer} primary="Online Ordering" />
                        <NavListItem to={paths.cart} onClick={this.closeDrawer} primary="Shopping Cart" />
                    </>}
                </List>
            </SwipeableDrawer>
            <AppBar classes={{ root: classes.appBar }} position='fixed'>
                <Toolbar>
                    <Tooltip disableInteractive arrow title="Menu">
                        <IconButton
                            color="inherit"
                            edge="start"
                            aria-label="open menu"
                            onClick={this.openDrawer}
                            size="large"
                        >
                            <MenuIcon />
                        </IconButton>
                    </Tooltip>
                    <Typography component={RouterLink} to={paths.root} variant="h6" noWrap className={classes.title}>{title}</Typography>
                    {features.ecommerce &&
                        <Tooltip disableInteractive arrow title="Shopping Cart">
                            <IconButton
                                color="inherit"
                                edge="end"
                                aria-label="cart"
                                onClick={() => {
                                    history.push(paths.cart)
                                }}
                                size="large"
                            >
                                <CartIcon />
                            </IconButton>
                        </Tooltip>
                    }
                    {user.isMultiStore && stores.length > 0 && <>
                        <Tooltip disableInteractive arrow title="Clinic Menu">
                            <IconButton
                                color="inherit"
                                edge="end"
                                aria-label="menu"
                                onClick={this.handleStoreMenuClick}
                                size="large"
                            >
                                <StoreIcon />
                            </IconButton>
                        </Tooltip>
                        <Menu anchorEl={storeMenuAnchor} open={!!storeMenuAnchor} onClose={this.closeStoreMenu}>
                            {stores.map(otherStore =>
                                <NavMenuItem
                                    key={otherStore.id}
                                    to={paths.root}
                                    onClick={() => { this.closeStoreMenu(); this.handleChangeStore(otherStore.id) }}
                                    disabled={otherStore.id == store.id}
                                >
                                    Switch to {otherStore.name}
                                </NavMenuItem>
                            )}
                        </Menu>
                    </>}
                    <Tooltip disableInteractive arrow title="Account Menu">
                        <IconButton
                            color="inherit"
                            edge="end"
                            aria-label="menu"
                            onClick={this.handleAccountMenuClick}
                            size="large"
                        >
                            <AccountCircleIcon />
                        </IconButton>
                    </Tooltip>

                    <Menu anchorEl={accountMenuAnchor} open={!!accountMenuAnchor} onClose={this.closeAccountMenu}>
                        <NavMenuItem onClick={this.closeAccountMenu} to={paths.account}>My Account</NavMenuItem>
                        {/* {features.ebooking &&
                            <NavMenuItem onClick={this.closeMenu} to={paths.appointments}>{langQuirks.my} Appointments</NavMenuItem>
                        } */}
                        {features.ecommerce &&
                            <NavMenuItem onClick={this.closeAccountMenu} to={paths.orders}>{langQuirks.my} Orders</NavMenuItem>
                        }
                        {features.ecommerce &&
                            <NavMenuItem onClick={this.closeAccountMenu} to={paths.paymentMethods}>{langQuirks.my} Payment Methods</NavMenuItem>
                        }
                        <NavMenuItem onClick={this.closeAccountMenu} to={paths.changePassword}>Change My Password</NavMenuItem>
                        <NavMenuItem onClick={() => { this.closeAccountMenu(); this.props.socket.send('logout') }} to={paths.root}>Logout</NavMenuItem>
                    </Menu>
                </Toolbar>
            </AppBar>
            <div className={classes.toolbar} />
            {remoteUser &&
                <AppBar classes={{ root: remoteUser ? classes.remoteUserAppBarPopulated : classes.remoteUserAppBar }} color="secondary" position="relative">
                    <Toolbar>
                        <RemoteUserPicker remoteUser={remoteUser} navigateTo={paths.root} />
                    </Toolbar>
                </AppBar>
            }
            <Box className={classes.outerBox}>
                <Box className={classes.innerBox}>
                    <Switch>
                        {/* Private views */}

                        <PrivateRoute path={paths.root} exact>
                            <Welcome />
                        </PrivateRoute>

                        <PrivateRoute path={paths.agenda}>
                            <Agenda />
                        </PrivateRoute>

                        <PrivateRoute path={paths.leadForm}>
                            <LeadForm />
                        </PrivateRoute>

                        {features.ebooking &&
                            <PrivateRoute path={paths.book}>
                                { remoteUser ? <Book /> : <MiniRemoteUserPicker /> }
                            </PrivateRoute>
                        }

                        {features.ecommerce &&
                            <PrivateRoute path={paths.order}>
                                { remoteUser ? <Order /> : <MiniRemoteUserPicker /> }
                            </PrivateRoute>
                        }

                        {features.ecommerce &&
                            <PrivateRoute path={paths.cart}>
                                { remoteUser ? <Cart /> : <MiniRemoteUserPicker /> }
                            </PrivateRoute>
                        }

                        {features.ecommerce &&
                            <PrivateRoute path={paths.orders}>
                                { remoteUser ? <Orders /> : <MiniRemoteUserPicker /> }
                            </PrivateRoute>
                        }

                        <PrivateRoute path={paths.account}>
                            <Account />
                        </PrivateRoute>

                        <PrivateRoute path={paths.verifySMS}>
                            <VerifySMS />
                        </PrivateRoute>

                        <PrivateRoute path={paths.paymentMethods}>
                            <PaymentMethods />
                        </PrivateRoute>

                        <PrivateRoute path={paths.changePassword}>
                            <ChangePassword />
                        </PrivateRoute>


                        {/* 404 Not Found */}

                        <Route>
                            <NotFound homePath={paths.root} />
                        </Route>
                    </Switch>
                </Box>

                <Box mt={1}>
                    <Copyright />
                </Box>
            </Box>

            {/* Reconnecting */}
            <Backdrop className={classes.reconnectingBackdrop} open={!connected}>
                <Snackbar open={!connected} message="Reconnecting..." />
            </Backdrop>
        </>
    }
}

export default withAppContext(withRouter(withStyles(styles, { withTheme: true })(App)))
